import { useState } from 'react';
import type { Category, AnswerValue } from '../types';
import { CATEGORIES, ANSWER_OPTIONS } from '../data/questions';
import type { SectionInfo } from '../lib/categoryUtils';
import QuestionInfo from './QuestionInfo';

interface FullReviewAccordionProps {
  sections: SectionInfo[];
  answers: Record<string, AnswerValue>;
  onAnswer: (questionId: string, value: AnswerValue) => void;
  onBackToQuiz: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  submitError: string | null;
}

export default function FullReviewAccordion({
  sections,
  answers,
  onAnswer,
  onBackToQuiz,
  onSubmit,
  isSubmitting,
  submitError,
}: FullReviewAccordionProps) {
  // Pre-expand sections that have unanswered questions
  const initialExpanded = new Set<Category>(
    sections
      .filter((s) => s.questions.some((q) => !(q.id in answers)))
      .map((s) => s.category),
  );
  const [expandedSections, setExpandedSections] = useState<Set<Category>>(initialExpanded);

  function toggleSection(category: Category) {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  }

  return (
    <div className="pb-24">
      <div className="space-y-2">
        {sections.map((section) => {
          const meta = CATEGORIES.find((c) => c.id === section.category);
          if (!meta) return null;

          const answeredCount = section.questions.filter((q) => q.id in answers).length;
          const totalCount = section.questions.length;
          const isExpanded = expandedSections.has(section.category);
          const hasGaps = answeredCount < totalCount;

          return (
            <div
              key={section.category}
              className="bg-purple-card rounded-xl border border-white/8 overflow-hidden"
            >
              {/* Accordion header */}
              <button
                onClick={() => toggleSection(section.category)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/5 transition-colors"
              >
                <span className="text-lg">{meta.emoji}</span>
                <span className="flex-1 text-sm font-medium text-white">
                  {meta.label}
                </span>
                <span className={`text-xs font-medium tabular-nums ${hasGaps ? 'text-amber-500' : 'text-green-500'}`}>
                  {answeredCount}/{totalCount}
                </span>
                <svg
                  className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Accordion body — only rendered when expanded */}
              {isExpanded && (
                <div className="px-4 pb-3 space-y-2">
                  {section.questions.map((q, idx) => {
                    const currentAnswer = answers[q.id] ?? null;
                    const isUnanswered = !currentAnswer;

                    return (
                      <div
                        key={q.id}
                        className={`rounded-lg p-3 ${
                          isUnanswered
                            ? 'border-l-2 border-amber-400 bg-amber-900/10'
                            : 'bg-white/5'
                        }`}
                      >
                        <p className="text-sm text-white mb-1">
                          <span className="text-slate-400 mr-1.5">{idx + 1}.</span>
                          {q.text}
                        </p>
                        {q.info && (
                          <div className="mb-1.5">
                            <QuestionInfo info={q.info} />
                          </div>
                        )}
                        {!q.info && <div className="mb-1" />}
                        <div className="flex gap-1.5">
                          {ANSWER_OPTIONS.map((opt) => {
                            const isSelected = currentAnswer === opt.value;
                            return (
                              <button
                                key={opt.value}
                                onClick={() => onAnswer(q.id, opt.value)}
                                className={`
                                  w-8 h-8 rounded-lg text-sm
                                  flex items-center justify-center
                                  transition-all duration-150
                                  ${
                                    isSelected
                                      ? 'bg-primary-500/20 ring-2 ring-primary-500 scale-110'
                                      : 'bg-white/5 hover:bg-white/10 hover:scale-105'
                                  }
                                `}
                                aria-label={opt.label}
                              >
                                {opt.emoji}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Sticky footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-deep-purple/90 backdrop-blur-sm border-t border-white/10 p-4 z-40">
        <div className="max-w-lg mx-auto flex flex-col gap-2">
          {submitError && (
            <div className="p-2 rounded-lg bg-red-900/20 border border-red-800">
              <p className="text-xs text-red-400">{submitError}</p>
            </div>
          )}
          <button
            onClick={onSubmit}
            disabled={isSubmitting}
            className="w-full px-6 py-3 gradient-cta disabled:opacity-50 text-white font-semibold rounded-xl glow-primary transition-all transform hover:-translate-y-0.5"
          >
            {isSubmitting ? 'Submitting...' : 'Submit my answers'}
          </button>
          <button
            onClick={onBackToQuiz}
            className="text-sm text-slate-400 hover:text-white transition-colors text-center"
          >
            Back to quiz
          </button>
        </div>
      </div>
    </div>
  );
}
